import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Chip, Switch,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Alert, CircularProgress, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { createFlow, updateFlow, getFlow } from '../api/whatsapp';

const FlowEditor = ({ id, onBack }) => {
  const isEdit = !!id;

  const [form, setForm] = useState({
    name: '',
    triggerKeywords: [],
    matchType: 'contains',
    greetingMessage: '',
    status: 'active',
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const data = await getFlow(id);
        if (data.flow) {
          setForm({
            name: data.flow.name || '',
            triggerKeywords: data.flow.triggerKeywords || [],
            matchType: data.flow.matchType || 'contains',
            greetingMessage: data.flow.greetingMessage || '',
            status: data.flow.status || 'active',
          });
        }
      } catch {
        setSnack({ open: true, message: 'Failed to load flow', severity: 'error' });
      }
      setLoading(false);
    })();
  }, [id, isEdit]);

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw) return;
    if (form.triggerKeywords.includes(kw)) return;
    setForm(prev => ({ ...prev, triggerKeywords: [...prev.triggerKeywords, kw] }));
    setKeywordInput('');
  };

  const removeKeyword = (kw) => {
    setForm(prev => ({ ...prev, triggerKeywords: prev.triggerKeywords.filter(k => k !== kw) }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSnack({ open: true, message: 'Flow name is required', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateFlow(id, form);
        setSnack({ open: true, message: 'Flow updated', severity: 'success' });
      } else {
        await createFlow(form);
        setSnack({ open: true, message: 'Flow created', severity: 'success' });
      }
      setTimeout(() => onBack?.(), 800);
    } catch {
      setSnack({ open: true, message: 'Failed to save flow', severity: 'error' });
    }
    setSaving(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack} sx={{ color: '#64748b' }}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight={700} color="#1e293b">{isEdit ? 'Edit Flow' : 'Create New Flow'}</Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Name */}
          <TextField label="Flow Name" fullWidth size="small" value={form.name}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Greeting Message, Support Reply"
          />

          {/* Trigger Keywords */}
          <Box>
            <Typography fontWeight={600} fontSize={14} color="#1e293b" sx={{ mb: 0.5 }}>Trigger Keywords</Typography>
            <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: 1 }}>
              When a user's message matches a keyword, this flow will auto-reply.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField size="small" placeholder="Type a keyword and press Enter" value={keywordInput}
                onChange={e => setKeywordInput(e.target.value)} onKeyDown={handleKeyDown}
                sx={{ flex: 1 }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 13 } }}
              />
              <Button variant="outlined" onClick={addKeyword} startIcon={<AddIcon />}
                sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {form.triggerKeywords.map((kw, i) => (
                <Chip key={i} label={kw} onDelete={() => removeKeyword(kw)} size="small"
                  sx={{ bgcolor: '#f0f2f5', color: '#1e293b', fontWeight: 500 }} />
              ))}
            </Box>
          </Box>

          {/* Match Type */}
          <FormControl size="small" sx={{ maxWidth: 250 }}>
            <InputLabel>Match Type</InputLabel>
            <Select value={form.matchType} label="Match Type"
              onChange={e => setForm(prev => ({ ...prev, matchType: e.target.value }))}
              sx={{ borderRadius: 2, fontSize: 13 }}>
              <MenuItem value="contains" sx={{ fontSize: 13 }}>Contains — message includes keyword</MenuItem>
              <MenuItem value="exact" sx={{ fontSize: 13 }}>Exact — message equals keyword</MenuItem>
            </Select>
          </FormControl>

          {/* Greeting Message */}
          <Box>
            <Typography fontWeight={600} fontSize={14} color="#1e293b" sx={{ mb: 0.5 }}>Greeting / Auto-Reply Message</Typography>
            <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: 1 }}>
              This message will be sent automatically when a trigger keyword matches.
            </Typography>
            <TextField multiline rows={4} fullWidth size="small" value={form.greetingMessage}
              onChange={e => setForm(prev => ({ ...prev, greetingMessage: e.target.value }))}
              placeholder="e.g. Hi! Thanks for reaching out. How can I help you today?"
              sx={{ '& textarea': { fontSize: 13 } }}
            />
          </Box>

          {/* Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch checked={form.status === 'active'}
              onChange={e => setForm(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }))}
              sx={{ '& .MuiSwitch-thumb': { bgcolor: form.status === 'active' ? '#075e54' : '#94a3b8' } }}
            />
            <Box>
              <Typography fontWeight={600} fontSize={14} color="#1e293b">{form.status === 'active' ? 'Active' : 'Inactive'}</Typography>
              <Typography variant="caption" color="#64748b">When active, this flow will auto-reply to matching messages</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onBack}
            sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#075e54', '&:hover': { bgcolor: '#054d44' } }}>
            {saving ? 'Saving...' : isEdit ? 'Update Flow' : 'Create Flow'}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FlowEditor;
